"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import {
  LuMapPin,
  LuBookOpen,
  LuGraduationCap,
  LuBuilding2,
  LuUsers,
  LuGlobe,
  LuMessageCircle,
} from "react-icons/lu";
import type { Representative } from "@/hooks/useRepresentatives";
import { useAuthStore } from "@/store/useAuthStore";
import { ComposePopover } from "./ComposePopover";

// ── Type metadata ────────────────────────────────────────────────────────────

const TYPE_META: Record<string, { label: string; Icon: React.ElementType }> = {
  University: { label: "University", Icon: LuBuilding2 },
  "Student Community": { label: "Student Community", Icon: LuUsers },
  Organization: { label: "Organization", Icon: LuGraduationCap },
};

// ── Shared sub-components ────────────────────────────────────────────────────

function Chip({
  color,
  icon: Icon,
  label,
}: {
  color: "indigo" | "purple";
  icon: React.ElementType;
  label: string;
}) {
  const cls =
    color === "indigo"
      ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-100 dark:border-indigo-500/20"
      : "bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-100 dark:border-purple-500/20";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium max-w-[130px] min-w-0 ${cls}`}
    >
      <Icon className="w-2.5 h-2.5 shrink-0" />
      <span className="truncate">{label}</span>
    </span>
  );
}

function OverflowBadge({ n }: { n: number }) {
  return (
    <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 text-xs shrink-0">
      +{n}
    </span>
  );
}

// ── RepresentativeCard ───────────────────────────────────────────────────────

export type RepresentativeCardProps = {
  representative: Representative;
  showTypeBadge?: boolean;
};

export function RepresentativeCard({
  representative: rep,
  showTypeBadge = false,
}: RepresentativeCardProps) {
  const { activeRole, accessToken } = useAuthStore();
  const isStudent = !activeRole || activeRole === "STUDENT";
  const canMessage = !!accessToken && isStudent && !!rep.userId;

  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const msgBtnRef = useRef<HTMLButtonElement>(null);

  const initials = rep.institutionName
    ? rep.institutionName
        .split(" ")
        .map((n) => n[0])
        .filter(Boolean)
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "R";

  const fields = Array.isArray(rep.fieldsOffered) ? rep.fieldsOffered : [];
  const accepting = Array.isArray(rep.countriesAccepting)
    ? rep.countriesAccepting
    : [];
  const services = Array.isArray(rep.servicesOffered)
    ? rep.servicesOffered
    : [];

  const typeMeta = rep.institutionType
    ? (TYPE_META[rep.institutionType] ?? null)
    : null;
  const TypeIcon = typeMeta?.Icon ?? LuGraduationCap;
  const contactName = `${rep.user.firstName} ${rep.user.lastName}`.trim();

  return (
    <>
    <Link
      href={`/representatives/${rep.id}`}
      className="group flex flex-col gap-3 p-5 rounded-2xl bg-white/80 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-violet-300 dark:hover:border-violet-500/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:hover:shadow-violet-500/5 min-w-0"
    >
      {/* ── Type badge (in flow, above header) ──────────────────────── */}
      {showTypeBadge && (
        <div className="flex justify-end mb-1">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300 text-[10px] font-semibold border border-violet-100 dark:border-violet-500/25 uppercase tracking-wide">
            <LuGraduationCap className="w-2.5 h-2.5" />
            Rep
          </span>
        </div>
      )}

      {/* ── Header: logo + name ──────────────────────────────────────── */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0">
          {rep.logo ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={rep.logo}
              alt={rep.institutionName}
              className="w-11 h-11 rounded-xl object-cover border-2 border-gray-200 dark:border-white/15 bg-white"
            />
          ) : (
            <div className="w-11 h-11 rounded-xl bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center text-violet-600 dark:text-violet-400 font-bold text-sm border-2 border-violet-200 dark:border-violet-500/30">
              {initials}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors truncate leading-snug">
              {rep.institutionName}
            </h3>
            {canMessage && (
              <button
                ref={msgBtnRef}
                type="button"
                aria-label={`Message ${rep.institutionName}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (anchorRect) {
                    setAnchorRect(null);
                  } else {
                    const rect = msgBtnRef.current?.getBoundingClientRect();
                    if (rect) setAnchorRect(rect);
                  }
                }}
                className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-lg border transition-colors ${
                  anchorRect
                    ? "bg-violet-600 border-violet-600 text-white"
                    : "text-violet-500 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-200 hover:bg-violet-50 dark:hover:bg-violet-500/15 border-violet-200 dark:border-violet-500/30"
                }`}
              >
                <LuMessageCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Type · contact on a single safe line */}
          <div className="flex items-center gap-1 mt-0.5 min-w-0 overflow-hidden">
            {typeMeta && (
              <span className="inline-flex items-center gap-0.5 text-xs text-violet-600 dark:text-violet-400 font-medium shrink-0">
                <TypeIcon className="w-3 h-3 shrink-0" />
                {typeMeta.label}
              </span>
            )}
            {typeMeta && contactName && (
              <span className="text-xs text-gray-300 dark:text-white/20 shrink-0 px-0.5">
                ·
              </span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {contactName}
            </span>
          </div>
        </div>
      </div>

      {/* ── Representative's own country ─────────────────────────────── */}
      {rep.country && (
        <div className="flex flex-wrap gap-1.5">
          <Chip color="indigo" icon={LuMapPin} label={rep.country} />
        </div>
      )}

      {/* ── Description ─────────────────────────────────────────────── */}
      {rep.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
          {rep.description}
        </p>
      )}

      {/* ── Fields offered ───────────────────────────────────────────── */}
      {fields.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {fields.slice(0, 2).map((f) => (
            <Chip key={f} color="purple" icon={LuBookOpen} label={f} />
          ))}
          {fields.length > 2 && <OverflowBadge n={fields.length - 2} />}
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <div className="pt-3 border-t border-gray-100 dark:border-white/8 flex items-center justify-between gap-2">
        {accepting.length > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 min-w-0">
            <LuGlobe className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              Accepts from {accepting.length}{" "}
              {accepting.length === 1 ? "country" : "countries"}
            </span>
          </span>
        ) : (
          <span />
        )}
        {services.length > 0 && (
          <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-xs font-medium border border-emerald-100 dark:border-emerald-500/20">
            {services.length} service{services.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </Link>

    {anchorRect && canMessage && rep.userId && (
      <ComposePopover
        expertUserId={rep.userId}
        expertName={rep.institutionName}
        anchorRect={anchorRect}
        onClose={() => setAnchorRect(null)}
      />
    )}
    </>
  );
}

// ── RepresentativeSkeletonCard ───────────────────────────────────────────────

export function RepresentativeSkeletonCard() {
  return (
    <div className="flex flex-col gap-3 p-5 rounded-2xl bg-white/80 dark:bg-white/5 border border-gray-200 dark:border-white/10 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        </div>
      </div>
      <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded-full" />
      <div className="space-y-1.5">
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
      </div>
      <div className="flex gap-1.5">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded-full"
          />
        ))}
      </div>
      <div className="pt-3 border-t border-gray-100 dark:border-white/8 flex items-center justify-between">
        <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
      </div>
    </div>
  );
}
